/* eslint-disable react/jsx-no-bind */
import React, { memo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import PropTypes from 'prop-types';
import {
  clearAreaSelection,
  closeAreaSelection,
  setLinkedElementInactive
} from '@actions/map-actions';
import ThemeStyle, { SECONDARY_TEXT } from '@components/theme-style';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import {
  getSelectionElementsCount,
  getSelectionParentElements,
  getExportableSelectionElements
} from '@selectors/map-selection';
import { getMapData } from '@selectors/map';
import LargeMedia from '@shared/large-media';
import SmallMedia from '@shared/small-media';
import IdentifyList from './identify-list';
import MobileIdentify from './mobile-identify';
import MobileNoResults from './mobile-no-results';
import {
  getTitles, getTypes, TITLE_FIELD
} from '@utils/map-data-utils';
import styles from './info-identify.scss';

const InfoIdentify = ({ mapRef }) => {
  const [parentEntity, setParentEntity] = useState(null);
  const dispatch = useDispatch();
  const mapSelectionCount = useSelector(getSelectionElementsCount);
  const mapSelection = useSelector(getSelectionParentElements);
  const mapData = useSelector(getMapData);
  const searchResultQuery = useSelector(state => state.map.searchResultQuery);
  const searchResultItems = useSelector(state => state.map.searchResultItems);
  const searchResultAutoExpandParent = useSelector(state => state.map.searchResultAutoExpandParent);

  useEffect(() => {
    setParentEntity(searchResultAutoExpandParent !== null ? searchResultAutoExpandParent : null);
  }, [searchResultItems, searchResultAutoExpandParent]);

  const onClear = () => dispatch(clearAreaSelection());
  const onClose = () => {
    dispatch(closeAreaSelection());
    dispatch(setLinkedElementInactive());
  };

  const onBack = () => {
    setParentEntity(null);
  };

  const exportSelection = useSelector(getExportableSelectionElements(parentEntity));
  const onExport = () => {
    for (const [entityType, rows] of Object.entries(exportSelection)) {
      const csvConfig = mkConfig({ useKeysAsHeaders: true, filename: `${entityType}-export` });
      const csv = generateCsv(csvConfig)(rows);
      download(csvConfig)(csv);
    }
  };

  let name;
  if (parentEntity) {
    const detail = mapSelection[parentEntity].items[0];
    const types = getTypes(mapData, detail);
    const {
      [TITLE_FIELD]: title
    } = getTitles(mapData, detail);
    if (title) {
      name = title;
    } else {
      name = types.pop();
    }
  }

  return (
    <div className={styles.infoIdentifyContainer} >
      <LargeMedia>
        {!parentEntity &&
          <div className={styles.identifyTitle}>
            {searchResultQuery || 'Identify results'}
            <div className={styles.identifyCount}>
              {mapSelectionCount}
            </div>
          </div>
        }
        {parentEntity &&
          <div className={styles.locationTitle}>
            <IconButton className={styles.backButton} onClick={onBack}>
              <Icon>arrow_back</Icon>
            </IconButton>
            <div className={styles.identifyLocation}>{name}</div>
          </div>
        }
        {mapSelectionCount === 0 &&
          <div className={styles.noResults}>
            No results identified within the boundaries
            <Button className={styles.clearButton} onClick={onClear}>
              <ThemeStyle style={SECONDARY_TEXT}>
                <div className={styles.clearLabel}>CLEAR BOUNDARIES</div>
              </ThemeStyle>
            </Button>
          </div>
        }
        <div className={styles.identifyResults}>
          <IdentifyList mapRef={mapRef} mapSelection={mapSelection} setParentEntity={setParentEntity} parentId={parentEntity} />
        </div>
        <IconButton className={styles.closeButton} onClick={onClose}>
          <Icon>close</Icon>
        </IconButton>
        {mapSelectionCount > 0 &&
          <IconButton className={styles.exportButton} onClick={onExport}>
            <Icon>download</Icon>
          </IconButton>
        }
      </LargeMedia>
      <SmallMedia>
        {mapSelectionCount > 0 ? 
          <MobileIdentify
            mapRef={mapRef}
            mapSelection={mapSelection}
            setParentEntity={setParentEntity}
            parentId={parentEntity}/> 
          : <MobileNoResults />}
      </SmallMedia>
    </div>
  );
};

InfoIdentify.propTypes = {
  mapRef: PropTypes.object.isRequired
};

export default memo(InfoIdentify);
